import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { FilePondOptions } from 'filepond';
import { FilePondComponent } from 'ngx-filepond';
import {
  RecordingUploadSession,
  ResumableUploadResponse,
  UploadSession,
} from '../../core/models/upload-session.model';
import { HttpClient } from '@angular/common/http';
import { getApiHostUrl, getUploadResumeURL } from '../../util/const';
import { ToastService } from '@black/ui';
import { CloudUploadService } from '../../core/services/cloud-upload.service';
import { RecordingSessionDTO } from '../../models/session-recording.model';
import { SessionSelection } from '../session-picker/session-picker.component';
import { ResourceService } from '../../core/state/resource/resource.service';
import { GoogleDrivePickerService } from '../../core/services/google-drive-picker.service';

@Component({
  selector: 'black-add-recordings',
  standalone: false,
  templateUrl: './add-recordings.component.html',
  styleUrl: './add-recordings.component.css',
})
export class AddRecordingsComponent {
  private fb = inject(NonNullableFormBuilder);
  private http = inject(HttpClient);
  private toastService = inject(ToastService);
  private uploadService = inject(CloudUploadService);
  private resourceService = inject(ResourceService);
  private drivePickerService = inject(GoogleDrivePickerService);

  drivePickerLoading = false;
  drivePickerError: string | null = null;

  @ViewChild('myPond')
  myPond!: FilePondComponent;

  @Input()
  liveClassId: string | undefined = '';

  currentUploadContext: null | {
    file: File;
    responseData: ResumableUploadResponse;
  } = null;

  selectedSession: SessionSelection | null = null;

  /** Top-level tab: 'file' = Add a File, 'external-url' = Add a URL */
  activeTab: 'file' | 'external-url' = 'file';

  /** Sub-mode within the "Add a File" tab */
  fileSubMode: 'device' | 'url-download' | 'google-drive' | null = null;

  switchTab(tab: 'file' | 'external-url') {
    this.activeTab = tab;
    this.fileSubMode = null;
    this.urlSubmitError = null;
    this.externalUrlSubmitError = null;
    this.drivePickerError = null;
  }

  selectFileSubMode(mode: 'device' | 'url-download' | 'google-drive') {
    this.fileSubMode = mode;
  }

  pondOptions: FilePondOptions = {
    allowMultiple: false,
    labelIdle: 'Drop files here or Browse',
    instantUpload: false,
    allowImagePreview: false,
    credits: false,
    acceptedFileTypes: ['video/*'],
  };

  recordUploadForm = this.fb.group({
    sessionName: ['', Validators.required],
    resource: ['', Validators.required],
  });

  urlRecordForm = this.fb.group({
    sessionName: ['', Validators.required],
    url: ['', [Validators.required, Validators.pattern(/^https?:\/\/[^\s/$.?#].[^\s]*$/)]],
  });

  urlSubmitting = false;
  urlSubmitError: string | null = null;

  externalUrlForm = this.fb.group({
    sessionName: ['', Validators.required],
    url: ['', [Validators.required, Validators.pattern(/^https?:\/\/[^\s/$.?#].[^\s]*$/)]],
  });

  externalUrlSubmitting = false;
  externalUrlSubmitError: string | null = null;

  driveRecordForm = this.fb.group({
    sessionName: ['', Validators.required],
  });

  activeSessions: RecordingUploadSession[] = [];
  currentSession: UploadSession | null = null;

  @Output()
  addRecording = new EventEmitter<RecordingUploadSession>();

  onAddRecording(recordingSession: RecordingUploadSession) {
    this.addRecording.emit(recordingSession);
  }

  onSessionSelected(selection: SessionSelection): void {
    this.selectedSession = selection;
  }

  switchMode(urlMode: boolean) {
    this.activeTab = urlMode ? 'external-url' : 'file';
    this.fileSubMode = null;
    this.urlSubmitError = null;
    this.externalUrlSubmitError = null;
  }

  pondHandleAddFile(event: any) {
    const file = event.file.file;

    this.http
      .post<ResumableUploadResponse>(getUploadResumeURL(), {
        name: file.name,
        type: file.type,
        size: file.size,
      })
      .subscribe((data) => {
        this.recordUploadForm.controls.resource.setValue(data.resourceId);
        this.currentUploadContext = { file, responseData: data };
      });
  }

  notifyUploadStarted() {
    this.toastService.showToast('New recording started uploading!', 'success');
  }

  uploadRecording(closeModal?: () => void) {
    this.recordUploadForm.markAllAsTouched();

    if (this.recordUploadForm.valid) {
      const formData = this.recordUploadForm.value;
      const sessionDate = this.selectedSession?.sessionDate ?? new Date();
      const meetingId = this.selectedSession?.meetingId ?? undefined;

      try {
        this.http
          .put<RecordingSessionDTO>(
            getApiHostUrl() +
              `/api/v1/live-class/${this.liveClassId}/recording`,
            {
              resourceId: formData.resource,
              sessionName: formData.sessionName,
              sessionDate,
              meetingId,
            }
          )
          .subscribe((data: RecordingSessionDTO) => {
            if (!this.currentUploadContext?.responseData)
              throw new Error('current session context is required!');

            const { file, responseData } = this.currentUploadContext;

            const uploadSessionId = this.uploadService.addUploadFromResponse(
              file,
              responseData,
              data.resource._id,
            );
            this.onAddRecording({
              sessionId: data._id,
              sessionName: data.sessionName,
              sessionDate: data.sessionDate,
              liveClass: data.liveClass,
              resource: data.resource,
              uploadSessionId,
              meetingId: data.meetingId,
            });
            this.notifyUploadStarted();

            // Reset the form and filepond
            this.recordUploadForm.reset();
            this.myPond.files = [];
            this.currentUploadContext = null;
            this.selectedSession = null;

            if (closeModal) closeModal();
          });
      } catch (error) {
        this.toastService.showToast('Recording upload failed!', 'error');
      }
    } else {
      this.toastService.showToast('Form is invalid!', 'error');
    }
  }

  submitUrlRecording(closeModal?: () => void) {
    this.urlRecordForm.markAllAsTouched();
    if (this.urlRecordForm.invalid) return;

    this.urlSubmitting = true;
    this.urlSubmitError = null;

    const { sessionName, url } = this.urlRecordForm.getRawValue();
    const sessionDate = this.selectedSession?.sessionDate ?? new Date();
    const meetingId = this.selectedSession?.meetingId ?? undefined;

    this.resourceService.uploadUrl({ name: sessionName, url, type: 'video' }).subscribe({
      next: ({ resourceId }) => {
        this.http
          .put<RecordingSessionDTO>(
            getApiHostUrl() + `/api/v1/live-class/${this.liveClassId}/recording`,
            { resourceId, sessionName, sessionDate, meetingId }
          )
          .subscribe({
            next: (data: RecordingSessionDTO) => {
              this.urlSubmitting = false;
              this.onAddRecording({
                sessionId: data._id,
                sessionName: data.sessionName,
                sessionDate: data.sessionDate,
                liveClass: data.liveClass,
                resource: data.resource,
                meetingId: data.meetingId,
              });
              this.toastService.showToast(
                'Recording URL queued for download!',
                'success'
              );
              this.urlRecordForm.reset();
              this.selectedSession = null;
              if (closeModal) closeModal();
            },
            error: () => {
              this.urlSubmitting = false;
              this.urlSubmitError = 'Failed to create recording. Please try again.';
            },
          });
      },
      error: () => {
        this.urlSubmitting = false;
        this.urlSubmitError = 'Failed to submit URL. Please try again.';
      },
    });
  }

  submitExternalUrlRecording(closeModal?: () => void) {
    this.externalUrlForm.markAllAsTouched();
    if (this.externalUrlForm.invalid) return;

    this.externalUrlSubmitting = true;
    this.externalUrlSubmitError = null;

    const { sessionName, url } = this.externalUrlForm.getRawValue();
    const sessionDate = this.selectedSession?.sessionDate ?? new Date();
    const meetingId = this.selectedSession?.meetingId ?? undefined;

    this.resourceService.uploadExternalUrl({ name: sessionName, url, type: 'video' }).subscribe({
      next: ({ resourceId }) => {
        this.http
          .put<RecordingSessionDTO>(
            getApiHostUrl() + `/api/v1/live-class/${this.liveClassId}/recording`,
            { resourceId, sessionName, sessionDate, meetingId }
          )
          .subscribe({
            next: (data: RecordingSessionDTO) => {
              this.externalUrlSubmitting = false;
              this.onAddRecording({
                sessionId: data._id,
                sessionName: data.sessionName,
                sessionDate: data.sessionDate,
                liveClass: data.liveClass,
                resource: data.resource,
                meetingId: data.meetingId,
              });
              this.toastService.showToast('External URL recording added!', 'success');
              this.externalUrlForm.reset();
              this.selectedSession = null;
              if (closeModal) closeModal();
            },
            error: () => {
              this.externalUrlSubmitting = false;
              this.externalUrlSubmitError = 'Failed to create recording. Please try again.';
            },
          });
      },
      error: () => {
        this.externalUrlSubmitting = false;
        this.externalUrlSubmitError = 'Failed to submit URL. Please try again.';
      },
    });
  }

  // ---- Google Drive Picker ----

  async selectFromGoogleDrive(closeModal?: () => void) {
    this.driveRecordForm.markAllAsTouched();
    if (this.driveRecordForm.invalid) return;

    this.drivePickerLoading = true;
    this.drivePickerError = null;

    try {
      const file = await this.drivePickerService.open();
      if (!file) {
        this.drivePickerLoading = false;
        return;
      }

      const sessionName = this.driveRecordForm.getRawValue().sessionName;
      const sessionDate = this.selectedSession?.sessionDate ?? new Date();
      const meetingId = this.selectedSession?.meetingId ?? undefined;

      this.resourceService
        .uploadGoogleDriveFile({
          fileId: file.id,
          accessToken: file.accessToken,
          name: file.name,
          mimeType: file.mimeType,
          type: 'video',
          access: 'private',
        })
        .subscribe({
          next: ({ resourceId }) => {
            this.http
              .put<RecordingSessionDTO>(
                getApiHostUrl() + `/api/v1/live-class/${this.liveClassId}/recording`,
                { resourceId, sessionName, sessionDate, meetingId }
              )
              .subscribe({
                next: (data: RecordingSessionDTO) => {
                  this.drivePickerLoading = false;
                  this.onAddRecording({
                    sessionId: data._id,
                    sessionName: data.sessionName,
                    sessionDate: data.sessionDate,
                    liveClass: data.liveClass,
                    resource: data.resource,
                    meetingId: data.meetingId,
                  });
                  this.toastService.showToast(
                    'Google Drive recording queued for upload. It will be available shortly.',
                    'success'
                  );
                  this.driveRecordForm.reset();
                  this.selectedSession = null;
                  this.fileSubMode = null;
                  if (closeModal) closeModal();
                },
                error: () => {
                  this.drivePickerLoading = false;
                  this.drivePickerError = 'Failed to create recording. Please try again.';
                },
              });
          },
          error: () => {
            this.drivePickerLoading = false;
            this.drivePickerError = 'Failed to start the Google Drive upload. Please try again.';
          },
        });
    } catch (err) {
      this.drivePickerLoading = false;
      this.drivePickerError = 'Could not open Google Drive picker. Ensure you are signed in and popups are allowed.';
    }
  }
}
